import type { NavItem, UserMenuItem } from '@/config/navigation'
import { useMemo } from 'react'
import { ArrowLeftIcon } from '@phosphor-icons/react'

import type { FeatureFlagKeyType, SectionKey } from '@scrambl/shared'
import {
  AccountType,
  ModuleTypeEnum,
  PermissionActionEnum,
} from '@scrambl/shared'

import { createLocalIcon } from '../components/local-icon'
import { useAccountType } from './useAccountType'
import { useEntityTerminology } from './useEntityTerminology'
import { useIsExternalUser } from './useIsExternalUser'
import { usePermissions } from './usePermissions'
import { useSupplierTerminology } from './useSupplierTerminology'

interface NavSection {
  key: SectionKey
  title?: string
  items: NavItem[]
  order: number
}

export function useNavigationItems() {
  const { Plural: EntitiesPlural } = useEntityTerminology()
  const { Plural: SuppliersPlural } = useSupplierTerminology()
  const { checkPermission } = usePermissions()
  const isExternalUser = useIsExternalUser()
  const { isPersonal } = useAccountType()
  const canApproveExpenses = checkPermission(
    ModuleTypeEnum.EXPENSE,
    PermissionActionEnum.APPROVE,
  )
  const canManageExpenses = checkPermission(
    ModuleTypeEnum.EXPENSE,
    PermissionActionEnum.MANAGE,
  )
  const canAccessFinanceExpenses = canApproveExpenses || canManageExpenses
  const canApproveTimesheets = checkPermission(
    ModuleTypeEnum.TIME_ENTRY,
    PermissionActionEnum.APPROVE,
  )
  const canReadTimesheets = checkPermission(
    ModuleTypeEnum.TIME_ENTRY,
    PermissionActionEnum.READ,
  )
  const canManageTimesheets = checkPermission(
    ModuleTypeEnum.TIME_ENTRY,
    PermissionActionEnum.MANAGE,
  )
  const canAccessTimesheets =
    canReadTimesheets || canApproveTimesheets || canManageTimesheets

  const navSections = useMemo<NavSection[]>(() => {
    const baseSections: NavSection[] = [
      {
        key: 'main',
        title: 'You',
        order: 1,
        items: [
          {
            href: '/today',
            label: 'Overview',
            icon: createLocalIcon('today-outline'),
            role: 'all',
          },
          {
            href: '/tasks/mine',
            label: isPersonal ? 'My Day' : 'My Work',
            icon: createLocalIcon('checkbox-outline'),
            role: 'all',
            permission: {
              module: ModuleTypeEnum.TASK,
              action: PermissionActionEnum.READ,
            },
          },
          {
            href: '/notes/mine',
            label: 'Notes',
            icon: createLocalIcon('sticky-note-outline'),
            role: 'all',
            featureFlag: 'NOTES' as FeatureFlagKeyType,
            permission: {
              module: ModuleTypeEnum.NOTE,
              action: PermissionActionEnum.READ,
            },
          },
          {
            href: '/my-time',
            label: 'Time',
            icon: createLocalIcon('time-outline'),
            role: 'all',
            featureFlag: 'TIME_TRACKING' as FeatureFlagKeyType,
          },
          {
            href: '/calendar',
            label: 'Calendar',
            icon: createLocalIcon('calendar-outline'),
            role: 'all',
            featureFlag: 'CALENDAR' as FeatureFlagKeyType,
          },
        ],
      },
      {
        key: 'work',
        title: 'Workspace',
        order: 2,
        items: [
          {
            href: '/my-organization',
            label: isPersonal ? 'Home Base' : 'My Organization',
            icon: createLocalIcon('organization-outline'),
            role: 'all',
            permission: {
              module: ModuleTypeEnum.ORGANIZATION,
              action: PermissionActionEnum.READ,
            },
          },
          {
            href: '/projects',
            label: 'Projects',
            icon: createLocalIcon('folder-outline'),
            role: 'all',
            featureFlag: 'PROJECTS' as FeatureFlagKeyType,
            permission: {
              module: ModuleTypeEnum.PROJECT,
              action: PermissionActionEnum.READ,
            },
          },
          {
            href: '/workstream',
            label: 'Workstream',
            icon: createLocalIcon('pulse-outline'),
            role: 'all',
            featureFlag: 'WORKSTREAM' as FeatureFlagKeyType,
            permission: {
              module: ModuleTypeEnum.WORKSTREAM,
              action: PermissionActionEnum.READ,
            },
          },
          {
            href: '/tasks',
            label: 'Tasks',
            icon: createLocalIcon('checkbox-outline'),
            role: 'all',
            featureFlag: 'TASKS' as FeatureFlagKeyType,
            permission: {
              module: ModuleTypeEnum.TASK,
              action: PermissionActionEnum.READ,
            },
          },
          {
            href: '/tickets',
            label: 'Tickets',
            icon: createLocalIcon('ticket-outline'),
            role: 'all',
            featureFlag: 'TICKETS' as FeatureFlagKeyType,
            subscriptionFeature: 'tickets',
            permission: {
              module: ModuleTypeEnum.TICKET,
              action: PermissionActionEnum.READ,
            },
          },
          {
            href: '/collections',
            label: 'Collections',
            icon: createLocalIcon('list-outline'),
            role: 'all',
            featureFlag: 'COLLECTIONS' as FeatureFlagKeyType,
            subscriptionFeature: 'collections',
            permission: {
              module: ModuleTypeEnum.CUSTOM_COLLECTION,
              action: PermissionActionEnum.READ,
            },
            hidden: true,
          },
          {
            href: '/forms',
            label: 'Forms',
            icon: createLocalIcon('clipboard-text-outline'),
            role: 'all',
            featureFlag: 'FORMS' as FeatureFlagKeyType,
            subscriptionFeature: 'forms',
            permission: {
              module: ModuleTypeEnum.FORM,
              action: PermissionActionEnum.READ,
            },
            hidden: true,
          },
          {
            href: '/threads',
            label: 'Discussions',
            icon: createLocalIcon('chatbubble-outline'),
            role: 'all',
            featureFlag: 'DISCUSSIONS' as FeatureFlagKeyType,
            subscriptionFeature: 'discussions',
            permission: {
              module: ModuleTypeEnum.THREAD,
              action: PermissionActionEnum.READ,
            },
            hidden: true,
          },
          {
            href: '/documents',
            label: 'Documents',
            icon: createLocalIcon('document-text-outline'),
            role: 'all',
            featureFlag: 'DOCUMENTS' as FeatureFlagKeyType,
            permission: {
              module: ModuleTypeEnum.DOCUMENT,
              action: PermissionActionEnum.READ,
            },
            hidden: true,
          },
          {
            href: '/events',
            label: 'Events',
            icon: createLocalIcon('calendar-outline'),
            role: 'all',
            featureFlag: 'EVENTS' as FeatureFlagKeyType,
            subscriptionFeature: 'events',
            permission: {
              module: ModuleTypeEnum.EVENT,
              action: PermissionActionEnum.READ,
            },
            hidden: true,
          },
        ],
      },
      {
        key: 'sales',
        title: 'Sales',
        order: 4,
        items: [
          // Contacts is the single CRM person surface. Leads are a saved view
          // inside Contacts, not a separate destination.
          {
            href: '/contacts',
            label: isPersonal ? 'Address Book' : 'Contacts',
            icon: createLocalIcon('person-outline'),
            role: 'all',
            featureFlag: 'CONTACTS' as FeatureFlagKeyType,
            permission: {
              module: ModuleTypeEnum.CONTACT,
              action: PermissionActionEnum.READ,
            },
          },
          {
            href: '/deals',
            label: 'Deals',
            icon: createLocalIcon('deal-outline'),
            role: 'marketing',
            featureFlag: 'DEALS' as FeatureFlagKeyType,
            subscriptionFeature: 'deals',
            permission: {
              module: ModuleTypeEnum.DEAL,
              action: PermissionActionEnum.READ,
            },
          },
        ],
      },
      {
        key: 'operations',
        title: 'Operations',
        order: 3,
        items: [
          {
            href: '/entities',
            label: EntitiesPlural,
            icon: createLocalIcon('building-outline'),
            role: 'all',
            featureFlag: 'ENTITIES' as FeatureFlagKeyType,
            permission: {
              module: ModuleTypeEnum.ENTITY,
              action: PermissionActionEnum.READ,
            },
          },
          {
            href: '/suppliers',
            label: SuppliersPlural,
            icon: createLocalIcon('building-outline'),
            role: 'all',
            featureFlag: 'SUPPLIERS' as FeatureFlagKeyType,
            subscriptionFeature: 'suppliers',
            permission: {
              module: ModuleTypeEnum.SUPPLIER,
              action: PermissionActionEnum.READ,
            },
          },
          {
            href: '/team',
            label: 'Team',
            icon: createLocalIcon('people-outline'),
            role: 'all',
            featureFlag: 'TEAM' as FeatureFlagKeyType,
            subscriptionFeature: 'team',
            permission: {
              module: ModuleTypeEnum.TEAM,
              action: PermissionActionEnum.READ,
            },
          },
          ...(canAccessTimesheets
            ? [
                {
                  href: '/timesheets',
                  label: 'Timesheets',
                  icon: createLocalIcon('time-outline'),
                  role: 'all' as const,
                  featureFlag: 'TIME_TRACKING' as FeatureFlagKeyType,
                },
              ]
            : []),
          {
            href: '/inventory',
            label: 'Inventory',
            icon: createLocalIcon('grid-outline'),
            role: 'all',
            featureFlag: 'INVENTORY' as FeatureFlagKeyType,
            permission: {
              module: ModuleTypeEnum.INVENTORY,
              action: PermissionActionEnum.READ,
            },
          },
          {
            href: '/workflows',
            label: 'Workflows',
            icon: createLocalIcon('link-outline'),
            role: 'all',
            featureFlag: 'WORKFLOWS' as FeatureFlagKeyType,
            subscriptionFeature: 'workflows',
            permission: {
              module: ModuleTypeEnum.WORKFLOW,
              action: PermissionActionEnum.READ,
            },
          },
        ],
      },
      {
        key: 'finance',
        title: 'Finance',
        order: 5,
        items: [
          {
            href: '/contracts',
            label: 'Contracts',
            icon: createLocalIcon('contract-outline'),
            role: 'all',
            featureFlag: 'CONTRACTS' as FeatureFlagKeyType,
            subscriptionFeature: 'contracts',
            permission: {
              module: ModuleTypeEnum.CONTRACT,
              action: PermissionActionEnum.READ,
            },
          },
          {
            href: '/invoices',
            label: 'Invoices',
            icon: createLocalIcon('receipt-outline'),
            role: 'all',
            featureFlag: 'INVOICES' as FeatureFlagKeyType,
            subscriptionFeature: 'advanced_reports',
            permission: {
              module: ModuleTypeEnum.INVOICE,
              action: PermissionActionEnum.READ,
            },
          },
          ...(canAccessFinanceExpenses
            ? [
                {
                  href: '/expenses',
                  label: 'Expenses',
                  icon: createLocalIcon('expense-outline'),
                  role: 'all' as const,
                  // Expenses is a business surface (TIME_TRACKING off for
                  // personal), but the org owner passes the expense permission
                  // check, so gate by account type to keep it out of personal.
                  accountTypes: [AccountType.ORGANIZATION],
                },
              ]
            : []),
        ],
      },
    ]
    if (isPersonal) {
      // Personal accounts collapse the Sales + Operations groups into a single
      // "People & Home" section holding the actual workspace members, address
      // book (Contacts), and household vendors (Suppliers). The Entities module
      // is business-only and is not repurposed for personal accounts. The
      // remaining Sales/Operations/Finance items are business-only and already
      // flag- or account-type-gated, so they don't need their own headers.
      const findItem = (href: string): NavItem | undefined =>
        baseSections
          .flatMap(section => section.items)
          .find(item => item.href === href)

      const peopleHomeItems = [
        {
          href: '/team',
          label: 'Members',
          icon: createLocalIcon('people-outline'),
          role: 'all' as const,
          featureFlag: 'TEAM' as FeatureFlagKeyType,
          permission: {
            module: ModuleTypeEnum.MEMBER,
            action: PermissionActionEnum.READ,
          },
        },
        findItem('/contacts'),
        findItem('/suppliers'),
      ].filter((item): item is NavItem => Boolean(item))

      const mainSection = baseSections.find(section => section.key === 'main')
      const workSection = baseSections.find(section => section.key === 'work')

      return [
        ...(mainSection ? [mainSection] : []),
        ...(workSection ? [workSection] : []),
        {
          key: 'operations',
          title: 'People & Home',
          order: 3,
          items: peopleHomeItems,
        },
      ]
    }
    if (isExternalUser) {
      // Hide only member-owned personal surfaces for external users.
      const externalDeniedMyRoutes = new Set(['/my-time'])
      return baseSections.map(section => ({
        ...section,
        items: section.items.filter(item => {
          if (!item.href?.startsWith('/my-')) return true
          return !externalDeniedMyRoutes.has(item.href)
        }),
      }))
    }
    return baseSections
  }, [
    EntitiesPlural,
    SuppliersPlural,
    canAccessFinanceExpenses,
    canAccessTimesheets,
    isExternalUser,
    isPersonal,
  ])

  // Add section information to each item
  const mainNavItems: NavItem[] = useMemo(
    () =>
      navSections
        .sort((a, b) => a.order - b.order)
        .flatMap(section =>
          section.items.map(item => ({
            ...item,
            section: section.key,
          })),
        ),
    [navSections],
  )

  const userMenuItems: UserMenuItem[] = [
    {
      href: '/user-settings',
      label: 'My Profile',
      icon: createLocalIcon('settings-outline'),
      divider: true,
    },
    {
      href: '/account',
      label: 'My Account',
      icon: createLocalIcon('person-circle-outline'),
    },
  ]

  // Admin navigation items
  const adminNavItems: NavItem[] = [
    {
      href: '/admin/members',
      label: 'Members',
      icon: createLocalIcon('people-outline'),
    },
    {
      href: '/admin/integrations',
      label: 'Integrations',
      icon: createLocalIcon('grid-outline'),
    },
    {
      href: '/admin/settings',
      label: 'Settings',
      icon: createLocalIcon('settings-outline'),
    },
    {
      href: '/admin/subscription',
      label: 'Subscription & Billing',
      icon: createLocalIcon('receipt-outline'),
      // Personal accounts are free by construction — they have no subscription
      // flag row, so billing surfaces must be gated by account type, not flags.
      accountTypes: [AccountType.ORGANIZATION],
    },
  ]

  // Back to app button for admin view
  const backToAppItem: NavItem = {
    href: '/today',
    label: 'Back to App',
    icon: ArrowLeftIcon,
  }

  // Export sections configuration for use in other components
  const sections = navSections.reduce(
    (acc, section) => ({
      ...acc,
      [section.key]: {
        key: section.key,
        title: section.title,
        order: section.order,
      },
    }),
    {} as Record<
      SectionKey,
      { key: SectionKey; title?: string; order: number }
    >,
  )

  return {
    mainNavItems,
    userMenuItems,
    adminNavItems,
    backToAppItem,
    sections,
  }
}
